// `slide-love-sentences` + its three reveals (`LOVE_MAX_REVEAL = 3`) — the streamlined script's
// replacement for the Korean "you" example (docs/PRESENTATION.md, "On language", 2026-08-09), recut
// by the 2026-08-11 polish pass (user stories 5/6/7):
//
//   step 0: **both** English sentences — "I love my family." / "I love tiramisu." Same word, two
//           meanings, and English never makes you say which. The slide is complete on arrival; it
//           used to look like it was missing its second half while the presenter set the example up.
//   step 1: the English/Greek split — one English word for love on the left, six Greek examples on the
//           right, with **no** emphasis on storgē yet. The audience counts, then the presenter picks.
//   step 2: storgē under "I love my family", and the same word marked in the Greek column.
//   step 3: hēdonē under "I love tiramisu."
//
// The slide is the talk's load-bearing example: it is what "translating into a more precise
// language forces someone to fill the gaps" means, and the Beekeeper guessing which love the
// presenter meant is the callback in chapter 3. So the two sentences are set at quote scale side
// by side, and each Greek word arrives **under the sentence it belongs to** rather than in a list.
//
// **Every revealed slot is reserved from step 0** — the two chip slots under the sentences and the
// split panel's own box are present and empty before their content lands, so nothing on the slide
// moves as the reveals arrive. `LOVE_NONE_OF_THESE` (a "none of these" caption that appeared under
// the tiramisu at the old step 1 and then vanished at step 2) is gone for the same reason.

import type { ReactElement, ReactNode } from 'react';
import { SlideFrame } from './SlideFrame';
import { SLIDE_SCALE } from './scale';
import {
  GREEK_LOVES,
  GREEK_LOVE_FOR_FAMILY,
  HEDONE,
  LOVE_MAX_REVEAL,
  LOVE_SENTENCES,
} from './content';
import './slides.css';

/** Tall enough for one `WordChip` — the reservation under each sentence. */
const CHIP_SLOT_HEIGHT = 128;
/** Tall enough for the split panel, so the sentences above it do not move when it arrives. */
const SPLIT_SLOT_HEIGHT = 260;

function WordChip({
  word,
  gloss,
  hot,
  italic = false,
}: {
  word: string;
  gloss: string;
  hot: boolean;
  italic?: boolean;
}): ReactElement {
  return (
    <div
      className={hot ? 'deck-hot-surface' : undefined}
      style={{
        fontFamily: 'var(--font-body)',
        background: hot ? undefined : 'var(--deck-card)',
        // Borderless when hot, like the Sunset/Octopus cards; the padding absorbs the 3px so the
        // chip's outer size is identical either way and nothing shifts as the reveals land.
        border: hot ? undefined : '3px solid var(--deck-card-border)',
        borderRadius: 'var(--r-md)',
        padding: hot ? '17px 25px' : '14px 22px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: SLIDE_SCALE.node,
          fontWeight: 600,
          color: hot ? 'var(--deck-card)' : 'var(--deck-ink)',
        }}
      >
        {italic ? <em>{word}</em> : word}
      </div>
      <div
        style={{
          fontSize: SLIDE_SCALE.edge,
          fontWeight: 600,
          color: hot ? 'var(--deck-card)' : 'var(--deck-ink)',
          marginTop: 4,
        }}
      >
        {gloss}
      </div>
    </div>
  );
}

function SentenceColumn({
  sentence,
  children,
}: {
  sentence: string;
  children?: ReactNode;
}): ReactElement {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 32,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: SLIDE_SCALE.quote,
          color: 'var(--deck-ink)',
          lineHeight: 'var(--leading-tight)',
          textAlign: 'center',
        }}
      >
        &ldquo;{sentence}&rdquo;
      </div>
      <div
        data-testid="love-chip-slot"
        style={{
          height: CHIP_SLOT_HEIGHT,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SplitHalf({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 600,
          fontSize: SLIDE_SCALE.label,
          letterSpacing: 'var(--tracking-caps)',
          textTransform: 'uppercase',
          color: 'var(--deck-caption)',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

export function LoveSlide({ revealStep }: { revealStep: number }): ReactElement {
  const step = Math.max(0, Math.min(revealStep, LOVE_MAX_REVEAL));
  const [family, tiramisu] = LOVE_SENTENCES;
  const familyLove = GREEK_LOVES.find((love) => love.word === GREEK_LOVE_FOR_FAMILY);

  return (
    <SlideFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 96 }}>
          <SentenceColumn sentence={family!}>
            {/* The observable for the `reveal-storge-for-family` cue (`cues.ts`): storgē
                attaching itself to this sentence is its own beat, one click after the six Greek
                words appear unmarked. */}
            {step >= 2 && familyLove !== undefined ? (
              <div data-testid="reveal-storge-for-family" className="demo-pop">
                <WordChip word={familyLove.word} gloss={familyLove.gloss} hot italic />
              </div>
            ) : undefined}
          </SentenceColumn>

          <SentenceColumn sentence={tiramisu!}>
            {step >= 3 ? (
              <div data-testid="reveal-hedone" className="demo-pop">
                <WordChip word={HEDONE.word} gloss={HEDONE.gloss} hot />
              </div>
            ) : undefined}
          </SentenceColumn>
        </div>

        {/* The English/Greek split: one word against six examples. Its box is reserved from step 0. */}
        <div
          data-testid="love-split-slot"
          style={{ height: SPLIT_SLOT_HEIGHT, display: 'flex', justifyContent: 'center' }}
        >
          {step >= 1 ? (
            <div
              data-testid="reveal-greek-loves"
              className="demo-reveal"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 72 }}
            >
              <SplitHalf label="English">
                <WordChip word="love" gloss="all of them" hot={false} />
              </SplitHalf>
              <SplitHalf label="Ancient Greek">
                <div
                  className="demo-stagger"
                  style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}
                >
                  {GREEK_LOVES.map((love) => (
                    <WordChip
                      key={love.word}
                      word={love.word}
                      gloss={love.gloss}
                      italic
                      // Not until step 2: at step 1 the audience is meant to see four equal options,
                      // not the answer.
                      hot={step >= 2 && love.word === GREEK_LOVE_FOR_FAMILY}
                    />
                  ))}
                </div>
              </SplitHalf>
            </div>
          ) : undefined}
        </div>
      </div>
    </SlideFrame>
  );
}
